import { useCallback, useEffect, useRef } from 'preact/hooks';

import { CANCELLED_MESSAGE, PAID_MESSAGE } from '@/routes/checkout/popup';

import { entitlementPollDelays } from '../core/entitlement';
import usePopupChannel from './usePopupChannel';

export type CheckoutPopupOutcome = 'paid' | 'cancelled' | 'abandoned';

interface UseCheckoutPopupOptions {
  /** Refetch the current user and answer whether the expected entitlement is now present. */
  checkEntitlement: () => Promise<boolean>;
  onConfirmed: () => void;
  /** Budget exhausted. Not an error — the card was charged (ADR 0003). */
  onUnconfirmed: () => void;
  onAbandoned: () => void;
}

const POPUP_TARGET = 'zl-speedy-checkout';

const CHECKOUT_MESSAGES = [PAID_MESSAGE, CANCELLED_MESSAGE] as const;

/**
 * Drives the Stripe checkout tab and works out, from this tab, whether the purchase actually
 * landed.
 *
 * The whole point is that neither the popup closing nor Stripe's redirect proves entitlement —
 * both race the `checkout.session.completed` webhook that writes the purchase row. So the popup's
 * message is only a signal to go and *look*, and looking means asking the API until the gate would
 * open (ADR 0003).
 */
const useCheckoutPopup = ({ checkEntitlement, onConfirmed, onUnconfirmed, onAbandoned }: UseCheckoutPopupOptions) => {
  const pollingRef = useRef(false);
  const cancelledRef = useRef(false);

  /**
   * Ask until the entitlement appears or the budget runs out.
   *
   * Waiting is gated on visibility: while the visitor is over in the Stripe tab this one is hidden,
   * and a request fired then could not possibly succeed. Becoming visible again short-circuits the
   * current wait rather than letting it run down.
   */
  const pollForEntitlement = useCallback(async () => {
    if (pollingRef.current) {
      return;
    }
    pollingRef.current = true;

    const waitVisible = (ms: number) =>
      new Promise<void>((resolve) => {
        let timer: ReturnType<typeof setTimeout>;
        const onVisible = () => {
          if (document.visibilityState !== 'visible') {
            return;
          }
          clearTimeout(timer);
          document.removeEventListener('visibilitychange', onVisible);
          window.removeEventListener('focus', onVisible);
          resolve();
        };
        const onTimeout = () => {
          document.removeEventListener('visibilitychange', onVisible);
          window.removeEventListener('focus', onVisible);
          resolve();
        };
        timer = setTimeout(onTimeout, ms);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
      });

    // The webhook usually beats the visitor back, so try once before waiting at all.
    if (await checkEntitlement()) {
      pollingRef.current = false;
      onConfirmed();
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const delay of entitlementPollDelays()) {
      if (cancelledRef.current) {
        pollingRef.current = false;
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await waitVisible(delay);
      if (document.visibilityState !== 'visible') {
        // eslint-disable-next-line no-continue
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      if (await checkEntitlement()) {
        pollingRef.current = false;
        onConfirmed();
        return;
      }
    }

    pollingRef.current = false;
    onUnconfirmed();
  }, [checkEntitlement, onConfirmed, onUnconfirmed]);

  /** A paid message is only ever a signal to go and look; a cancellation is the visitor's answer. */
  const handleMessage = useCallback(
    (type: string) => {
      if (type === PAID_MESSAGE) {
        pollForEntitlement();
      } else {
        onAbandoned();
      }
    },
    [onAbandoned, pollForEntitlement],
  );

  /**
   * Closed without telling us anything. They may still have paid and shut the tab before Stripe's
   * redirect landed, so this checks once rather than assuming either way.
   */
  const handleClosed = useCallback(async () => {
    if (await checkEntitlement()) {
      onConfirmed();
    } else {
      onAbandoned();
    }
  }, [checkEntitlement, onAbandoned, onConfirmed]);

  const {
    open: openChannel,
    reserve,
    release,
    stop: stopChannel,
  } = usePopupChannel({
    target: POPUP_TARGET,
    messages: CHECKOUT_MESSAGES,
    onMessage: handleMessage,
    onClosed: handleClosed,
  });

  /**
   * Stop everything. Called on unmount and once an outcome is reached.
   *
   * The channel's own teardown covers the watcher and the listener; the two flags here are what
   * halts the polling loop, which outlives them — it is an async walk over `entitlementPollDelays`
   * and has no listener to remove.
   */
  const stop = useCallback(() => {
    pollingRef.current = false;
    cancelledRef.current = true;
    stopChannel();
  }, [stopChannel]);

  useEffect(() => stop, [stop]);

  /**
   * Open checkout in a new tab and start watching.
   *
   * The tab reserved during the click is reused if there is one — see `usePopupChannel.reserve`.
   * `false` means the browser blocked it, and the caller falls back to a same-tab redirect carrying
   * the flow state.
   */
  const open = useCallback(
    (checkoutUrl: string) => {
      cancelledRef.current = false;
      return openChannel(checkoutUrl);
    },
    [openChannel],
  );

  return { open, reserve, release, retry: pollForEntitlement, stop };
};

export default useCheckoutPopup;
